'use client';

import React from 'react';
import { Box, Button, FormLabel, MenuItem, TextField, Typography } from '@mui/material';
import { useResellerAccess } from '@crm-src/components/reseller';

export interface AddressFields {
  company: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface BillToBulkUpdate {
  bill_to_company?: string;
  bill_to_address?: string;
  bill_to_city?: string;
  bill_to_state?: string;
  bill_to_zip?: string;
  bill_to_country?: string;
}

export interface ShipToBulkUpdate {
  ship_to_company?: string;
  ship_to_address?: string;
  ship_to_city?: string;
  ship_to_state?: string;
  ship_to_zip?: string;
  ship_to_country?: string;
}

/** Human label for a customer contact person in the recipient dropdown —
 * "Name Surname • email" so the whole recipient reads inside the dropdown. */
function contactOptionLabel(c: any): string {
  const name =
    c.display_name ||
    c.full_name ||
    [c.first_name, c.last_name].filter(Boolean).join(' ').trim() ||
    'Contact';
  return c.email ? `${name} • ${c.email}` : name;
}

/**
 * Contact-person picker for a Bill To / Ship To / "To" recipient. Options are
 * scoped to ONE company's own contacts (the selected customer's
 * `customer_contacts`) — never the global contact list. The chosen person's
 * name (in the dropdown) and email sit on ONE row so the recipient reads as a
 * single line; a long email truncates with an ellipsis (full value on hover).
 */
const ContactPersonField: React.FC<{
  label: string;
  contacts: any[];
  value: number | null;
  onChange: (contactId: number | null) => void;
}> = ({ label, contacts, value, onChange }) => (
  <Box>
    <FormLabel>{label}</FormLabel>
    <TextField
      select
      size="small"
      fullWidth
      SelectProps={{ displayEmpty: true }}
      value={value != null ? String(value) : ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
    >
      <MenuItem value="">— None —</MenuItem>
      {contacts.map((c) => (
        <MenuItem key={c.id} value={String(c.id)}>
          {contactOptionLabel(c)}
        </MenuItem>
      ))}
    </TextField>
  </Box>
);

interface BillToShipToCardProps {
  documentType: 'invoice' | 'quote';
  selectedContact: any | null;
  billToFields: AddressFields;
  shipToFields: AddressFields;
  onBillToChange: (field: keyof AddressFields, value: string) => void;
  onShipToChange: (field: keyof AddressFields, value: string) => void;
  onBillToBulkChange: (updates: BillToBulkUpdate) => void;
  onShipToBulkChange: (updates: ShipToBulkUpdate) => void;
  billToRecipientType: 'reseller' | 'dealer' | null;
  setBillToRecipientType: (value: 'reseller' | 'dealer' | null) => void;
  selectedDealerId: string | null;
  setSelectedDealerId: (value: string | null) => void;
  // Recipient contact person per section (scoped to the customer's contacts).
  // The email now renders inside the dropdown option label, not as a prop here.
  billToContactId: number | null;
  shipToContactId: number | null;
  onBillToContactChange: (contactId: number | null) => void;
  onShipToContactChange: (contactId: number | null) => void;
}

export const BillToShipToCard: React.FC<BillToShipToCardProps> = ({
  documentType,
  selectedContact,
  billToFields,
  shipToFields,
  onBillToChange,
  onShipToChange,
  onBillToBulkChange,
  onShipToBulkChange,
  billToRecipientType,
  setBillToRecipientType,
  selectedDealerId,
  setSelectedDealerId,
  billToContactId,
  shipToContactId,
  onBillToContactChange,
  onShipToContactChange,
}) => {
  // Reseller wall: a viewer without crm:resellers can't bill-to a reseller or
  // its dealers (dealers are reseller-scoped) — the recipient dropdown is hidden
  // and the invoice bills the customer directly. See components/reseller.tsx.
  // (Hook must precede the early return below to satisfy the Rules of Hooks.)
  const canViewResellers = useResellerAccess();

  // The recipient contact persons come from the SELECTED customer's own contact
  // list only — never the global directory.
  const customerContacts: any[] = selectedContact?.customer_contacts || [];

  const updateBillToFields = (recipientType: 'reseller' | 'dealer' | 'customer', dealerId?: string | null) => {
    if (!selectedContact) return;

    const updateData: BillToBulkUpdate = {};

    if (recipientType === 'reseller' && selectedContact.reseller) {
      // Bill To = Reseller - Update ALL address fields
      updateData.bill_to_company = selectedContact.reseller.name || '';
      updateData.bill_to_address = selectedContact.reseller.address || '';
      updateData.bill_to_city = selectedContact.reseller.city || '';
      updateData.bill_to_state = selectedContact.reseller.state || '';
      updateData.bill_to_zip = selectedContact.reseller.postal_code || '';
      updateData.bill_to_country = selectedContact.reseller.country || 'U.S.A';

    } else if (recipientType === 'dealer' && dealerId && selectedContact.dealers) {
      // Bill To = Selected Dealer - Update ALL address fields
      const dealer = selectedContact.dealers.find((d: any) => d.id.toString() === dealerId);
      if (dealer) {
        updateData.bill_to_company = dealer.name || '';
        updateData.bill_to_address = dealer.address || '';
        updateData.bill_to_city = dealer.city || '';
        updateData.bill_to_state = dealer.state || '';
        // Handle both postal_code and zip for compatibility
        updateData.bill_to_zip = dealer.postal_code || dealer.zip || '';
        updateData.bill_to_country = dealer.country || 'U.S.A';

      }
    } else {
      // Fallback to customer address - Update ALL address fields
      updateData.bill_to_company = selectedContact.company_name || '';
      updateData.bill_to_address = selectedContact.address || '';
      updateData.bill_to_city = selectedContact.city || '';
      updateData.bill_to_state = selectedContact.state || '';
      // Handle both zip and postal_code for compatibility
      updateData.bill_to_zip = selectedContact.zip || selectedContact.postal_code || '';
      updateData.bill_to_country = selectedContact.country || 'U.S.A';
    }

    onBillToBulkChange(updateData);
  };

  // Copy bill to address to ship to address
  const copyBillToShipTo = () => {
    onShipToBulkChange({
      ship_to_company: billToFields.company,
      ship_to_address: billToFields.address,
      ship_to_city: billToFields.city,
      ship_to_state: billToFields.state,
      ship_to_zip: billToFields.zip,
      ship_to_country: billToFields.country,
    });
  };

  // The Bill To RECIPIENT dropdown: the customer's reseller or one of its
  // dealers. Shown only when the customer has one AND the viewer has reseller
  // access (reseller wall). Since 2026-09-12 the pick is STORED
  // (`bill_to_reseller_id`), so the document lists on that reseller's profile —
  // which is why quotes get the dropdown too, next to their contact person.
  const recipientSelect =
    canViewResellers && selectedContact && (selectedContact.reseller || (selectedContact.dealers && selectedContact.dealers.length > 0)) ? (
      <TextField
        select
        size="small"
        fullWidth
        label={documentType === 'invoice' ? undefined : 'Bill to'}
        SelectProps={{ displayEmpty: true }}
        value={billToRecipientType === 'reseller' ? 'reseller' : billToRecipientType === 'dealer' ? `dealer-${selectedDealerId}` : ''}
        onChange={(e) => {
          const value = e.target.value;
          if (value === 'reseller') {
            setBillToRecipientType('reseller');
            setSelectedDealerId(null);
            updateBillToFields('reseller');
          } else if (value.startsWith('dealer-')) {
            const dealerId = value.replace('dealer-', '');
            setBillToRecipientType('dealer');
            setSelectedDealerId(dealerId);
            updateBillToFields('dealer', dealerId);
          } else {
            setBillToRecipientType(null);
            setSelectedDealerId(null);
            updateBillToFields('customer');
          }
        }}
        inputProps={{ 'data-cy': 'bill-to-recipient' }}
      >
        <MenuItem value="">{documentType === 'invoice' ? 'Select recipient' : 'The customer'}</MenuItem>
        {selectedContact.reseller && (
          <MenuItem value="reseller">
            Reseller: {selectedContact.reseller.name}
          </MenuItem>
        )}
        {selectedContact.dealers && selectedContact.dealers.map((dealer: any) => (
          <MenuItem key={dealer.id} value={`dealer-${dealer.id}`}>
            Dealer: {dealer.name}
          </MenuItem>
        ))}
      </TextField>
    ) : null;

  // Quote: no Bill To / Ship To addresses — the recipient contact person (it
  // rides the bill_to side, like the quote PDF's "To" block) and, when the
  // customer has one, WHICH reseller the quote is addressed to. The PARENT
  // renders this right under the customer selector (it IS the customer's
  // contact person), so there is deliberately no "To" section heading here.
  if (documentType !== 'invoice') {
    return (
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: recipientSelect ? { xs: "1fr", md: "1fr 1fr" } : "1fr" }}>
        <ContactPersonField
          label="Contact Person"
          contacts={customerContacts}
          value={billToContactId}
          onChange={onBillToContactChange}
        />
        {recipientSelect}
      </Box>
    );
  }

  return (
    <>
      {/* Bill To Address - Only show for invoices, not quotes */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 600 }}>Bill To</Typography>
          {/* Bill To Recipient Dropdown - Only show if customer has reseller or
              dealers, AND the viewer has reseller access (reseller wall). */}
          {recipientSelect && <Box sx={{ flex: 1, maxWidth: 320 }}>{recipientSelect}</Box>}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <ContactPersonField
            label="Contact Person"
            contacts={customerContacts}
            value={billToContactId}
            onChange={onBillToContactChange}
          />
          <Box>
            <FormLabel>Company Name</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={billToFields.company}
              onChange={(e) => onBillToChange('company', e.target.value)}
              placeholder="Company Name"
            />
          </Box>
          <Box>
            <FormLabel>Street Address</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={billToFields.address}
              onChange={(e) => onBillToChange('address', e.target.value)}
              placeholder="Street Address"
            />
          </Box>
          <Box>
            <FormLabel>City</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={billToFields.city}
              onChange={(e) => onBillToChange('city', e.target.value)}
              placeholder="City"
            />
          </Box>
          <Box>
            <FormLabel>State</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={billToFields.state}
              onChange={(e) => onBillToChange('state', e.target.value)}
              placeholder="State"
            />
          </Box>
          <Box>
            <FormLabel>ZIP Code</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={billToFields.zip}
              onChange={(e) => onBillToChange('zip', e.target.value)}
              placeholder="ZIP Code"
            />
          </Box>
          <Box>
            <FormLabel>Country</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={billToFields.country}
              onChange={(e) => onBillToChange('country', e.target.value)}
              placeholder="Country"
            />
          </Box>
        </Box>
      </Box>

      {/* Ship To Address - Only show for invoices, not quotes */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 600 }}>Ship To</Typography>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            size="small"
            onClick={copyBillToShipTo}
          >
            Copy from Bill To
          </Button>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <ContactPersonField
            label="Contact Person"
            contacts={customerContacts}
            value={shipToContactId}
            onChange={onShipToContactChange}
          />
          <Box>
            <FormLabel>Company Name</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={shipToFields.company}
              onChange={(e) => onShipToChange('company', e.target.value)}
              placeholder="Company Name"
            />
          </Box>
          <Box>
            <FormLabel>Street Address</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={shipToFields.address}
              onChange={(e) => onShipToChange('address', e.target.value)}
              placeholder="Street Address"
            />
          </Box>
          <Box>
            <FormLabel>City</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={shipToFields.city}
              onChange={(e) => onShipToChange('city', e.target.value)}
              placeholder="City"
            />
          </Box>
          <Box>
            <FormLabel>State</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={shipToFields.state}
              onChange={(e) => onShipToChange('state', e.target.value)}
              placeholder="State"
            />
          </Box>
          <Box>
            <FormLabel>ZIP Code</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={shipToFields.zip}
              onChange={(e) => onShipToChange('zip', e.target.value)}
              placeholder="ZIP Code"
            />
          </Box>
          <Box>
            <FormLabel>Country</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={shipToFields.country}
              onChange={(e) => onShipToChange('country', e.target.value)}
              placeholder="Country"
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};
